// GE-CAPPS — AppRunner générique : rend une app custom (spec déclarative
// construite via le connecteur MCP Claude) à l'URL /apps/:slug.
//
// Le composant ne connaît RIEN du contenu : il charge l'app
// (GET /api/customapps/apps/:slug/), exécute ses vues
// (POST .../data/) puis rend chaque vue selon son type :
//   * table — MUI DataGrid (tri/filtre pilotables par colonne, règle
//     gridCols LAP-551 : valueGetter brut / valueFormatter affiché) ;
//   * chart — Recharts (donut/pie/bar/line/area) sur rows [{label,value}] ;
//   * kpi   — carte valeur unique (format money/number/percent/text).
// Le tout dans une grille react-grid-layout (trame MonPatrimoine), positions
// initiales depuis spec.layout. Un sélecteur de période global relance les
// vues avec l'override `period` (même whitelist que la spec côté moteur).
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Responsive, WidthProvider } from 'react-grid-layout'
import { DataGrid } from 'Components/LifeDataGrid'
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts'

import { makeGetRequest, makePostRequest } from 'utils/api'
import { sharedDataGridSx } from '../Investments/_shared/theme'
import {
  PERIODS,
  specCol,
  formatKpi,
  formatValue,
  CHART_COLORS,
  appIcon,
} from './runnerShared'
import './CustomApps.css'

const ResponsiveGridLayout = WidthProvider(Responsive)

/* ── Tooltip Recharts (DA sombre ca-) ─────────────────────────────── */
function CaTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null
  const entry = payload[0]
  return (
    <div className="ca-tooltip">
      <div className="ca-tooltip-label">{entry.payload?.label ?? label}</div>
      <div className="ca-tooltip-value">
        {formatValue(entry.value, 'number')}
      </div>
    </div>
  )
}

/* ── Vue graphique ─────────────────────────────────────────────────── */
function ChartView({ result }) {
  const kind = result.chart?.kind || 'bar'
  const rows = result.rows || []

  if (kind === 'donut' || kind === 'pie') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={rows}
            dataKey="value"
            nameKey="label"
            innerRadius={kind === 'donut' ? '55%' : 0}
            outerRadius="85%"
            paddingAngle={rows.length > 1 ? 2 : 0}
            stroke="none"
          >
            {rows.map((row, i) => (
              <Cell
                key={row.label}
                fill={CHART_COLORS[i % CHART_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip content={<CaTooltip />} />
          <Legend
            wrapperStyle={{ fontSize: 11, color: '#9c9db8' }}
            iconSize={9}
          />
        </PieChart>
      </ResponsiveContainer>
    )
  }

  const axes = (
    <>
      <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
      <XAxis
        dataKey="label"
        tick={{ fill: '#63648a', fontSize: 11 }}
        axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
        tickLine={false}
      />
      <YAxis
        tick={{ fill: '#63648a', fontSize: 11 }}
        axisLine={false}
        tickLine={false}
        width={46}
      />
      <Tooltip content={<CaTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
    </>
  )

  if (kind === 'line') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          {axes}
          <Line
            type="monotone"
            dataKey="value"
            stroke="#6fa0ff"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    )
  }
  if (kind === 'area') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          {axes}
          <Area
            type="monotone"
            dataKey="value"
            stroke="#6fa0ff"
            strokeWidth={2}
            fill="rgba(111,160,255,0.18)"
          />
        </AreaChart>
      </ResponsiveContainer>
    )
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        {axes}
        <Bar dataKey="value" fill="#6fa0ff" radius={[4, 4, 0, 0]} maxBarSize={42} />
      </BarChart>
    </ResponsiveContainer>
  )
}

/* ── Vue table ─────────────────────────────────────────────────────── */
function TableView({ result }) {
  const columns = useMemo(
    () => (result.columns || []).map(specCol),
    [result.columns],
  )
  return (
    <div className="no-drag" style={{ flex: 1, minHeight: 0 }}>
      <DataGrid
        rows={result.rows || []}
        columns={columns}
        density="compact"
        disableSelectionOnClick
        rowsPerPageOptions={[25, 50, 100]}
        initialState={{ pagination: { pageSize: 25 } }}
        sx={sharedDataGridSx}
      />
    </div>
  )
}

/* ── Carte d'une vue (dispatch par type) ───────────────────────────── */
function ViewCard({ result, t }) {
  const body = () => {
    if (result.error) {
      return <div className="ca-view-error no-drag">{result.error}</div>
    }
    if (result.type === 'kpi') {
      return (
        <div className="ca-kpi">
          <div className="ca-kpi-value">
            {formatKpi(result.value, result.kpi)}
          </div>
          <div className="ca-kpi-label">{result.title}</div>
        </div>
      )
    }
    if (result.type === 'chart') return <ChartView result={result} />
    if (result.type === 'table') return <TableView result={result} />
    return null
  }

  const total =
    result.type === 'table' && typeof result.total === 'number'
      ? `${result.total} ${t('customapps.rows', 'lignes')}`
      : null

  return (
    <div className="ca-card ca-view-card">
      <div className="ca-view-head">
        <h3 className="ca-view-title">{result.title || result.id}</h3>
        {total && <span className="ca-view-total">{total}</span>}
      </div>
      <div className="ca-view-body">{body()}</div>
    </div>
  )
}

/* ── Layout initial : spec.layout, sinon empilement plein largeur ──── */
function buildLayout(spec, results) {
  const placed = {}
  ;(spec?.layout || []).forEach((item) => {
    placed[item.view] = item
  })
  let nextY = 0
  return results.map((r) => {
    const item = placed[r.id]
    const defaultH = r.type === 'kpi' ? 3 : 8
    const out = item
      ? { i: r.id, x: item.x, y: item.y, w: item.w, h: item.h }
      : { i: r.id, x: 0, y: nextY, w: 12, h: defaultH }
    if (!item) nextY += defaultH
    return { ...out, minW: 2, minH: 2 }
  })
}

/* ── Page ──────────────────────────────────────────────────────────── */
export function AppRunner() {
  const { slug } = useParams()
  const { t } = useTranslation()
  const [app, setApp] = useState(null)
  const [results, setResults] = useState([])
  const [period, setPeriod] = useState(null) // null = périodes de la spec
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const runViews = useCallback(
    async (overrides) => {
      // Sans override : exécution de toutes les vues telles que la spec les
      // définit. Avec override période : on relance chaque vue une à une
      // (le endpoint « toutes les vues » n'accepte pas d'overrides).
      if (!overrides) {
        const resp = await makePostRequest(
          `/api/customapps/apps/${slug}/data/`,
          {},
          {},
          true,
        )
        if (resp?.status === 200) return resp.data.views || []
        throw new Error(resp?.data?.detail || 'App execution failed.')
      }
      const current = await makeGetRequest(
        `/api/customapps/apps/${slug}/`,
        {},
        true,
      )
      const views = current?.data?.spec?.views || []
      return Promise.all(
        views.map(async (v) => {
          const resp = await makePostRequest(
            `/api/customapps/apps/${slug}/data/`,
            { view_id: v.id, overrides },
            {},
            true,
          )
          if (resp?.status === 200) return resp.data
          return {
            id: v.id,
            type: v.type,
            title: v.title,
            error: resp?.data?.detail || 'View execution failed.',
          }
        }),
      )
    },
    [slug],
  )

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      const resp = await makeGetRequest(`/api/customapps/apps/${slug}/`, {}, true)
      if (cancelled) return
      if (resp?.status !== 200) {
        setError(
          resp?.status === 404
            ? t('customapps.notFound', "Cette app n'existe pas (ou plus).")
            : t('customapps.loadError', "Impossible de charger l'app."),
        )
        setLoading(false)
        return
      }
      setApp(resp.data)
      try {
        const views = await runViews(null)
        if (!cancelled) setResults(views)
      } catch (e) {
        if (!cancelled) setError(String(e.message || e))
      }
      if (!cancelled) setLoading(false)
    }
    load()
    return () => {
      cancelled = true
    }
  }, [slug, runViews, t])

  const onPeriod = async (key) => {
    const next = key === period ? null : key
    setPeriod(next)
    setLoading(true)
    try {
      const views = await runViews(next ? { period: next } : null)
      setResults(views)
    } catch (e) {
      setError(String(e.message || e))
    }
    setLoading(false)
  }

  const layout = useMemo(() => buildLayout(app?.spec, results), [app, results])

  return (
    <div className="ca">
      <div className="ca-main">
        <Link to="/apps" className="ca-back">
          ← {t('customapps.backToApps', 'Mes Apps')}
        </Link>

        {error && (
          <div className="ca-card ca-empty">
            <h2>{t('customapps.errorTitle', 'Oups')}</h2>
            <p>{error}</p>
          </div>
        )}

        {!error && app && (
          <>
            <div className="ca-runner-head">
              <div
                className="ca-app-icon"
                style={{ background: app.color || '#6fa0ff' }}
              >
                {appIcon(app.icon)}
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div className="ca-header" style={{ marginBottom: 0 }}>
                  <div>
                    <h1>{app.name}</h1>
                    {app.description && (
                      <p className="ca-subtitle">{app.description}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="ca-periods" style={{ marginBottom: 16 }}>
              {PERIODS.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`ca-period-chip${period === key ? ' is-active' : ''}`}
                  onClick={() => onPeriod(key)}
                >
                  {t(`customapps.period.${key}`, label)}
                </button>
              ))}
            </div>
          </>
        )}

        {loading && (
          <div className="ca-loading">
            <span className="ca-spinner" />
            {t('customapps.loading', 'Exécution des vues…')}
          </div>
        )}

        {!loading && !error && app && (
          <ResponsiveGridLayout
            className="layout"
            layouts={{ lg: layout }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={44}
            useCSSTransforms
            draggableCancel=".no-drag"
          >
            {results.map((r) => (
              <div key={r.id}>
                <ViewCard result={r} t={t} />
              </div>
            ))}
          </ResponsiveGridLayout>
        )}
      </div>
    </div>
  )
}

export default AppRunner
